import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'

import MarkdownRender from 'components/Luxkit/MarkdownRender'
import { rem } from 'polished'
import PaneDetailsSection from 'components/Common/Pane/PaneDetailsSection'
import List from 'components/Common/List'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'

import OfferPageRoomGallery from '../OfferPageRoomGallery'
import Pane from 'components/Common/Pane'
import useToggle from 'hooks/useToggle'
import { sortBy, take } from 'lib/arrayUtils'
import { OfferPackageOptionView } from 'selectors/offerPageSelectors'
import FlashHotelRateOption from './FlashHotelRateOption'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import LineCameraIcon from 'components/Luxkit/Icons/line/LineCameraIcon'
import { ImageParams } from 'components/Common/ResponsiveImage'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import { fireInteractionEvent, fireNonInteractionEvent } from 'api/googleTagManager'
import { calendarAction, urgencyLabelImpression } from 'analytics/eventDefinitions'
import { useInView } from 'react-intersection-observer'
import { connect } from 'react-redux'
import TextButton from 'components/Luxkit/Button/TextButton'
import { OFFER_TYPE_HOTEL_ANALYTICS } from 'constants/offer'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import TitleGrid from '../PackageOption/TitleGrid'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import useLowestPackagePrices from 'components/OfferPage/hooks/useLowestPackagePrices'
import ImageGalleryLightbox from 'components/Luxkit/Carousel/ImageGalleryLightbox'
import LightBoxContext from 'components/Luxkit/Modal/LightBoxContext'
import SolidBoltAltIcon from 'components/Luxkit/Icons/solid/SolidBoltAltIcon'
import InlineGroup from 'components/Common/InlineGroup/InlineGroup'
import { isFlightForceBundled } from 'lib/isFlightForceBundled'

const xsHorizontalPadding = 24
const smHorizontalPadding = 28
const mdHorizontalPadding = 40

const ContentGrid = styled.div`
  display: grid;
  grid-row-gap: ${rem(16)};
  grid-template-columns: 1fr;
  padding: ${rem(16)} ${rem(xsHorizontalPadding)};

  ${mediaQueryUp.sm} {
    grid-template-columns: 1fr ${rem(230)};
    grid-row-gap: ${rem(24)};
    padding: ${rem(16)} ${rem(smHorizontalPadding)};
    padding-bottom: ${rem(24)};
  }

  ${mediaQueryUp.md} {
    grid-column-gap: ${rem(24)};
    padding: ${rem(28)} ${rem(mdHorizontalPadding)};
    padding-bottom: ${rem(16)};
    grid-template-columns: ${rem(400)} 1fr;
  }
`

const TitleSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;

  > * + * {
    margin-top: ${rem(12)};
  }

  ${mediaQueryUp.sm} {
    grid-column: 1 / span 2;
  }

  ${mediaQueryUp.md} {
    text-align: left;
    grid-column: 1;

    > * + * {
      margin-top: 0;
    }
  }
`

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;

  grid-column: 1;
  grid-row: 3;

  > * + * {
    margin-top: ${rem(12)};
  }

  ${mediaQueryUp.sm} {
    align-items: flex-start;
    grid-column: 1 / span 2;
  }

  ${mediaQueryUp.md} {
    grid-column: 2;
    grid-row: 1;
  }
`

const Description = styled.div`
  > * + * {
    margin-top: ${rem(8)};

    ${mediaQueryUp.md} {
      margin-top: ${rem(12)};
    }
  }
`

const BookingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: left;

  ${mediaQueryUp.md} {
    margin-top: auto;
    justify-self: flex-end;
    align-self: flex-end;
  }

  ${mediaQueryDown.sm} {
    width: 100%;
  }

  > * + * {
    margin-top: ${rem(8)};
  }
`

const ViewPhotosButton = styled(TextButton)`
  display: flex;
  margin-left: auto;
  margin-top: ${rem(24)};
`

const DetailsPane = styled.div`
  padding: ${rem(24)};

  > * + * {
    margin-top: ${rem(16)};
  }

  ${mediaQueryUp.sm} {
    padding: ${rem(36)} ${rem(48)};
    display: flex;

    > * {
      margin-top: 0;
      flex: 1 1 50%;

      &:nth-child(2) {
        /* First child is the close cross */
        padding-right: ${rem(30)};
      }

      &:last-child {
        padding-left: ${rem(30)};
      }
    }
  }
`

const DetailsDescription = styled(MarkdownRender)`
  margin-top: ${rem(12)};
`

const AmenityList = styled(List)`
  margin-top: ${rem(12)};
  margin-bottom: 0;

  ${mediaQueryUp.sm} {
    columns: 2;
  }
`

const RateSection = styled.div`
  > * {
    border-top: 1px solid ${props => props.theme.palette.neutral.five};
  }
`

const ShowMoreContent = styled(SmallToggleButton)`
  padding: ${rem(12)};
  width: 100%;
  justify-content: center;
`

const BlockCSSBreakpoint = styled(CSSBreakpoint)`
  width: 100%;
`

const MobileTitleCSSBreakpoint = styled(BlockCSSBreakpoint)`
  grid-column: 1 / span 2;
`

const RoomImages = styled(OfferPageRoomGallery)`
  height: 100%;
  margin-left: -${rem(xsHorizontalPadding)};
  width: calc(100% + ${rem(xsHorizontalPadding * 2)});
  flex: 1;

  ${mediaQueryUp.sm} {
    margin-left: -${rem(smHorizontalPadding)};
    width: calc(100% + ${rem(smHorizontalPadding * 2)});
  }

  ${mediaQueryUp.md} {
    margin-top: -${rem(8)};
    margin-left: -${rem(mdHorizontalPadding / 2)};
    width: calc(100% + ${rem(mdHorizontalPadding / 2)});
  }
`

const BookingMessageWrapper = styled.div`
  display: flex;
  align-items: center;

  ${mediaQueryDown.sm} {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    padding: ${rem(16)} 0 ${rem(8)};
    border-top: 1px solid ${props => props.theme.palette.neutral.five};
  }
`

const BookingSecondaryButton = styled(TextButton)`
  ${mediaQueryDown.sm} {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.md} {
    margin-left: ${rem(8)};
  }
`

interface Props {
  offer: App.Offer;
  packageViews: Array<OfferPackageOptionView>;
  flightDestination?: App.Airport;
  previewMode: boolean;
  bookedToday?: number;
}

const initialRatesToShow = 3
const imageProps: ImageParams = {
  xs: '32:13',
  sm: '35:12',
  md: '40:21',
  mdWidth: '420px',
  quality: 'good',
}

function FlashHotelRoomOption(props: Props) {
  const {
    offer,
    packageViews,
    flightDestination,
    previewMode,
    bookedToday,
  } = props

  const pkg = packageViews[0].package
  const [showingMoreRates, showMoreRates] = useToggle()
  const [showDetails, onToggleDetails] = useToggle()
  const offerPageDispatch = useOfferPageDispatch()
  const showLightbox = useContext(LightBoxContext)
  const openGallery = () => showLightbox(<ImageGalleryLightbox images={pkg.roomType.images} />)

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    offerPageDispatch({ type: OfferPageActions.setRoom, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterTypes })
  }, [offerPageDispatch, pkg.roomType])

  const onToggleFilterSoldOut = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    fireInteractionEvent(calendarAction(
      OFFER_TYPE_HOTEL_ANALYTICS,
      CALENDAR_GA_EVENT_ACTION.OpenFilter,
      CALENDAR_GA_EVENT_LABEL.PackageOptionSoldOut))
    onToggleFilter(e)
  }, [onToggleFilter])

  const sortedAmenities = useMemo(() => sortBy(pkg.roomType.amenities, a => a.name, 'asc'), [pkg.roomType])
  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable),
  ], [packageViews])

  const availablePackageViews = useMemo(() => packageViews.filter(view => view.capacityValid && view.hotelAvailable), [packageViews])
  const availableForPurchase = useIsAvailableForPurchase()

  const { ref: urgencyLabelsDesktopRef, inView: urgencyLabelsInView } = useInView({ triggerOnce: true })

  useEffect(() => {
    if (urgencyLabelsInView) {
      fireNonInteractionEvent(urgencyLabelImpression())
    }
  }, [urgencyLabelsInView])

  const bookedTodayLabel = bookedToday >= 3 ? (
    <LabelGroup>
      <UrgencyLabel variant="critical1" ref={urgencyLabelsDesktopRef}>
        <InlineGroup vertical="center" spacing="xs">
          <SolidBoltAltIcon size="XS" />
          Booked {bookedToday} times today
        </InlineGroup>
      </UrgencyLabel>
    </LabelGroup>
  ) : undefined

  /**
   * Preview is used only by content people.
   * We don't want to show unavailable rateOptions to the user.
   */
  const rateOptionViews = previewMode ? packageViews : availablePackageViews

  const { price, value } = useLowestPackagePrices(rateOptionViews)

  return (
    <Pane shadow data-testid="package-option">
      <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
      <ContentGrid>
        <MobileTitleCSSBreakpoint max="sm">
          <TitleGrid
              title={pkg.roomType.name ? <Heading variant="heading4">{pkg.roomType.name}</Heading> : undefined}
              urgencyLabel={bookedTodayLabel}
              bookmarkButton={availableForPurchase && datesAvailable && roomFitsCapacity && (
                <TripGuard>
                  <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} price={price} value={value}/>
                </TripGuard>
              )}
              bookedToday={!!bookedTodayLabel}
            />
        </MobileTitleCSSBreakpoint>
        <TitleSection>
          {pkg.roomType.images.length > 0 && <RoomImages
            room={pkg.roomType}
            imageProps={imageProps}
            offer={offer}
          />}
        </TitleSection>
        <DescriptionSection>
          <BlockCSSBreakpoint min="md">
            <TitleGrid
              title={pkg.roomType.name ? <Heading variant="heading4">{pkg.roomType.name}</Heading> : undefined}
              urgencyLabel={bookedTodayLabel}
              bookmarkButton={availableForPurchase && datesAvailable && roomFitsCapacity && (
                <TripGuard>
                  <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} price={price} value={value}/>
                </TripGuard>
              )}
              bookedToday={!!bookedTodayLabel}
            />
          </BlockCSSBreakpoint>
          <Description>
            { !datesAvailable && <UrgencyLabel variant="critical2">SOLD OUT</UrgencyLabel>}
            {pkg.roomType.description && <BodyText variant="medium">{pkg.roomType.description}</BodyText>}
            {pkg.roomType.sizeSqm &&
              <BodyText variant="medium">
                <strong>Size:</strong> {pkg.roomType.sizeSqm} m&sup2;
              </BodyText>
            }
          </Description>
          <SmallToggleButton size="small" onClick={onToggleDetails} on={showDetails}>
            {showDetails ? 'Hide' : 'View'} room details
          </SmallToggleButton>
          {availableForPurchase &&
            <BookingSection>
              { !datesAvailable && <BookingMessageWrapper>
                <BodyText weight="normal" variant="medium">
                  This room is sold out on your dates.
                </BodyText>
                <BookingSecondaryButton kind="secondary" onClick={onToggleFilterSoldOut} data-filter="checkin">
                  View available dates
                </BookingSecondaryButton>
              </BookingMessageWrapper> }
              { !roomFitsCapacity && datesAvailable && <BookingMessageWrapper>
                <BodyText weight="normal" variant="medium">
                  Max number of guests exceeded.
                </BodyText>
                {!isFlightForceBundled(offer) && (<BookingSecondaryButton kind="secondary" onClick={onToggleFilter} data-filter="capacity">
                  Add another {offer.saleUnit}
                </BookingSecondaryButton>)}
              </BookingMessageWrapper> }
            </BookingSection>
          }
        </DescriptionSection>
      </ContentGrid>
      <PaneDetailsSection isOpen={showDetails} onToggle={onToggleDetails}>
        <DetailsPane>
          <div>
            <BodyText variant="large" weight="bold">Room facilities:</BodyText>
            <BodyText variant="medium" >
              <AmenityList>
                {sortedAmenities.map(amenity =>
                  <li key={amenity.id}>{amenity.name}</li>,
                )}
              </AmenityList>
            </BodyText>
          </div>
          <div>
            {pkg.roomPolicyDescription && <>
              <BodyText variant="medium" weight="bold">Room policies:</BodyText>
              <DetailsDescription fontSize="medium" content={pkg.roomPolicyDescription} type="compact" />
            </>}
            <ViewPhotosButton kind="tertiary" outdent="left" startIcon={<LineCameraIcon />} onClick={openGallery}>
              View room photos
            </ViewPhotosButton>
          </div>
        </DetailsPane>
      </PaneDetailsSection>
      <RateSection>
        {take(rateOptionViews, showingMoreRates ? 100 : 3).map((view, index) => <FlashHotelRateOption
          key={view.package.uniqueKey}
          offer={offer}
          optionNumber={index + 1}
          packageView={view}
          flightDestination={flightDestination}
        />)}
        {rateOptionViews.length > initialRatesToShow &&
          <ShowMoreContent
            on={showingMoreRates}
            onClick={showMoreRates}
            size="small"
          >
            {showingMoreRates ? 'Show less options' : 'Show all options'}
          </ShowMoreContent>
        }
      </RateSection>
    </Pane>
  )
}

function mapStateToProps(state: App.State) {
  return {
    previewMode: state.config.previewMode,
  }
}

export default connect(mapStateToProps)(FlashHotelRoomOption)
